import React from "react";
import PropTypes from "prop-types";
// Limitless imports
import { useTranslation } from "@limitless/common/hooks";
import Grid from "@limitless/modern/layout/Grid";
import Typography from "@limitless/modern/data-display/typography/Typography";
import CodeCollector from "@limitless/modern/inputs/code-collector/CodeCollector";
import Button from "@limitless/modern/inputs/button/Button";
import SuccessAnimation from "@limitless/modern/components/success-animation/SuccessAnimation";
import { PAYPAL_WAITTING_FOR_CODE, PAYPAL_PROCESS_COMPLETED, PAYPAL_PROCESS_INITIATED } from "@limitless/expert";

const PaypalVerification = ({
    status,
    onVerifyClick,
    paypalAccount,
    verifyPaypalError,
    clearVerifyPaypalError,
    onCancel,
    setIsEditPaypalAddress,
    profile,
    setIsSuccess,
    isSuccess
}) => {
    const { t } = useTranslation();
    const [verificationCode, setVerificationCode] = React.useState("");
    const [disabled, setDisabled] = React.useState(false);
    const [showResendText, setShowResendText] = React.useState(false);

    const { state } = status;

    React.useEffect(() => {
        if (verifyPaypalError) {
            clearVerifyPaypalError();
        }
    }, [verificationCode]);

    React.useEffect(() => setDisabled(false), [verifyPaypalError]);

    React.useEffect(() => {
        if (state === PAYPAL_PROCESS_COMPLETED) {
            setIsSuccess(true);
            setTimeout(() => {
                setIsSuccess(false);
                onCancel();
            }, 4000);
        }
    }, [state]);

    const handleSendOtpClick = () => {
        setShowResendText(true);
        onVerifyClick();
    };

    const renderVerificationSuccess = () => (
        <>
            <Grid item xs={9.5} sx={{ margin: "auto" }}>
                <SuccessAnimation />
            </Grid>
            <Grid sx={{ textAlign: "center" }} item xs={6}>
                <Grid>
                    <Typography variant="caption" translationSubstitutes={paypalAccount.value}>
                        {t("Your email {0}")}
                    </Typography>
                </Grid>
                <Typography variant="caption">{t("You can now withdraw your rewards!")}</Typography>
            </Grid>
        </>
    );

    const renderVarificationCodeInput = () => (
        <>
            <Grid>
                <Typography variant="caption" sx={{ color: theme => theme.palette.grey[4] }}>
                    {t("A verification code has been sent to")}{" "}
                    <Typography variant="caption" fontWeight="fontWeightRegular">
                        {paypalAccount.verificationStage === 0 && profile.paypalAccount
                            ? profile.paypalAccount
                            : paypalAccount.value}
                    </Typography>
                </Typography>
            </Grid>
            <Typography variant="caption" sx={{ color: theme => theme.palette.grey[4] }}>
                {t("Please enter your code below:")}
            </Typography>
            <Grid mt={theme => theme.spacing(1.5)}>
                <CodeCollector
                    max={4}
                    onUpdate={value => {
                        if (showResendText) {
                            setShowResendText(false);
                        }
                        setVerificationCode(value);
                    }}
                    clearAll={showResendText}
                />
            </Grid>
            <Grid mt={theme => theme.spacing(2)} item>
                <Button
                    variant="contained"
                    id="verifyButton"
                    disabled={verificationCode.length !== 4 || disabled}
                    onClick={() => {
                        setDisabled(true);
                        onVerifyClick({ verificationCode });
                        setIsEditPaypalAddress(true);
                    }}
                >
                    {t("Verify")}
                </Button>
            </Grid>
            <Grid mt={theme => theme.spacing(1.6)}>
                {!verifyPaypalError && !showResendText ? (
                    <>
                        <Typography variant="caption" display="inline" sx={{ color: theme => theme.palette.grey[4] }}>
                            {t("Didn’t get the code?")}{" "}
                        </Typography>
                        <Typography
                            variant="caption"
                            display="inline"
                            color="primary"
                            sx={{ cursor: "pointer" }}
                            onClick={handleSendOtpClick}
                        >
                            {t("Resend")}
                        </Typography>
                    </>
                ) : showResendText ? (
                    <Typography variant="caption">{t("A new code has been sent")}</Typography>
                ) : (
                    <>
                        <Typography variant="caption" sx={{ color: theme => theme.palette.error.dark }}>
                            {verifyPaypalError?.message ||
                                t("The verification code is not valid. Please try again or ")}
                        </Typography>
                        <Grid>
                            <Typography
                                variant="caption"
                                color="primary"
                                sx={{ cursor: "pointer" }}
                                onClick={handleSendOtpClick}
                            >
                                {t("send a new code")}
                            </Typography>
                        </Grid>
                    </>
                )}
            </Grid>
        </>
    );

    return (
        <Grid mt={theme => theme.spacing(2)}>
            {(state === PAYPAL_WAITTING_FOR_CODE || state === PAYPAL_PROCESS_INITIATED) &&
                renderVarificationCodeInput()}
            {isSuccess && renderVerificationSuccess()}
        </Grid>
    );
};

PaypalVerification.propTypes = {
    clearVerifyPaypalError: PropTypes.func,
    verifyPaypalError: PropTypes.object,
    paypalAccount: PropTypes.string.isRequired,
    onVerifyClick: PropTypes.func.isRequired,
    dialogContentClass: PropTypes.string,
    status: PropTypes.object.isRequired,
    onCancel: PropTypes.func.isRequired,
    setIsEditPaypalAddress: PropTypes.bool,
    profile: PropTypes.object,
    setIsSuccess: PropTypes.func,
    isSuccess: PropTypes.bool
};

export default PaypalVerification;
